"use client";

import { useEffect, useRef } from "react";

/** How much of the film must be on screen to start it. */
const VISIBLE_RATIO = 0.25;

/**
 * A decorative, muted, looping film that **costs nothing until it is seen** (PERF-03).
 *
 * It used to be `autoPlay` with `preload="auto"` and sat below the fold, so the browser downloaded
 * its 5.7 MB on every home load, competing with the page's own first paint. Now it is
 * `preload="none"`: the file is not requested until the film enters the screen, where it starts, and
 * it pauses when it leaves, so a visitor who never scrolls down never pays for it. The poster is the
 * film's own first frame, in AVIF, so nothing changes when it starts.
 *
 * Without `IntersectionObserver` (or when `play()` is refused) it simply stays on its poster.
 */
export function LazyLoopVideo({ src, poster, className }: { src: string; poster: string; className?: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video === null || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            // A refused play (autoplay policy, a failed read) leaves the poster: nothing to handle.
            void video.play()?.catch(() => {});
          } else {
            video.pause();
          }
        }
      },
      { threshold: VISIBLE_RATIO },
    );
    observer.observe(video);

    return () => observer.disconnect();
  }, []);

  return (
    <video ref={videoRef} muted loop playsInline preload="none" poster={poster} className={className}>
      <source src={src} type="video/mp4" />
    </video>
  );
}
